import { observer } from "mobx-react-lite";
import { nanoid } from "nanoid";
import { useContext, useEffect, useMemo, useState } from "react";
import { getCourierDeliveryCost } from "../../http/basketApi";
import { fetchCitysForOrderModal } from "../../http/settingsApi";
import { ReactComponent as AlertIcon } from "../../assets/icons/alert.svg";
import { Context } from "../../index";
import AddressInput from "../AddressInput/AddressInput";
import CitySelectionModal from "../SelectCity/SelectCity";
import ButtonUI from "../UI/ButtonUI/ButtonUI";
import InputUI from "../UI/Input/Input";
import {useMediaQuery} from "../../hooks/useMediaQuery";

function DeliveryFormBase({ onSubmitCallback, withSelectCity = false, errorAddress = false, preorderMode = false }) {
  const { delivery, basket } = useContext(Context);
  const [newAddressData, setNewAddressData] = useState(null);
  const [apartment, setApartment] = useState("");
  const [costResult, setCostResult] = useState(0);
  const [comment, setComment] = useState("");
  const [isFetchCourier, setIsFetchCourier] = useState(false);
  const [isCityModalOpen, setIsCityModalOpen] = useState(false);
  const [cityForOrder, setCityForOrder] = useState(null);
  const [isEdit, setIsEdit] = useState(false);
  const isTablet = useMediaQuery('(max-width: 992px)')

  const notRu = useMemo(() => {
    if (delivery.cityInfo) {
      return delivery.cityInfo.data.country_iso_code !== "RU";
    }
    return true;
  }, [delivery.cityInfo]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newAddressData) {
      delivery.setAddressValidShield("Пожалуйста, укажите адрес");
      return;
    }

    const DTO = {
      type: "courier",
      addressFields: {
        ...newAddressData,
        apartment,
        comment,
        cityInfo: delivery.cityInfo,
      },
      city: delivery.cityInfo?.value,
    };
    
    
    delivery.setOfficePvz(null);
    delivery.setSelectedAddress(DTO);
    setDelivery()
    setNewAddressData(null)
    if (onSubmitCallback) onSubmitCallback();
  };

  const setDelivery = () => {
    if (preorderMode) {
      const methods = delivery.allMethods?.courier || [];
      const selected = methods.find(method => !method.has_fitting && !method.is_express) || methods[0];
      delivery.setActive(selected || null, 0);
      delivery.setError(selected ? '' : 'Доставка пока недоступна');
      delivery.setHasError(!selected);
      return;
    }
    if (costResult.error) {
      delivery.setActive(null, 0);
      delivery.setError(costResult.error);
      delivery.setHasError(true);
    }

    const selectedDelivery = delivery.allMethods.courier.find(
        (del) =>
            del.delivery_type === costResult.type || del.target === costResult.type
    );

    if (selectedDelivery) {
      // delivery.setPrice(costResult.cost);
      delivery.setActive(selectedDelivery, costResult.cost);

      delivery.setError("");
      delivery.setHasError(false);
    }
  }

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!newAddressData) {
      delivery.setAddressValidShield("Пожалуйста, укажите адрес");
      return;
    }

    const DTO = {
      type: "courier",
      addressFields: {
        ...newAddressData,
        apartment,
        comment,
        cityInfo: delivery.cityInfo,
      },
      city: delivery.cityInfo?.value,
    };

    delivery.setSelectedAddress(DTO);
    delivery.setOfficePvz(null);
    setDelivery()
    if (onSubmitCallback) onSubmitCallback();
  };

  const handleCitySelect = (item) => {
    if (!item.data) item = { value: item.name, data: item };
    delivery.setCityInfo(item);
    setIsCityModalOpen(false);
  };

  const IS_VALID_ADDRESS = useMemo(() => {
    
    if (notRu) {
      return newAddressData;
    } else {
      return (
        newAddressData &&
        newAddressData.street &&
        newAddressData.house &&
        !delivery.hasError &&
        !isFetchCourier
      );
    }
  }, [newAddressData, delivery.hasError, isFetchCourier]);

  useEffect(() => {
    if (!newAddressData || preorderMode) return;

    const fetchCost = async () => {
      setIsFetchCourier(true);

      const costResult = await getCourierDeliveryCost(
        newAddressData.all,
        basket.priceNoSell
      );

      setCostResult(costResult)

      setIsFetchCourier(false);
    };

    fetchCost();
  }, [newAddressData, basket.priceNoSell, delivery, preorderMode]);

  useEffect(() => {
    setIsEdit(false)
    fetchCitysForOrderModal().then((response) => {
      if (!response.error) setCityForOrder(response);
    });
  }, []);

  useEffect(() => {
    const city = delivery.cityInfo;
    const editData = delivery.editDataDelivery || delivery.selectedAddress;


    if (city) setCityForOrder(city);
    if (editData) {
      setNewAddressData(editData.addressFields || editData);
      setApartment(editData.addressFields?.apartment || editData.apartment);
      setComment(editData.addressFields?.comment || editData.comment);
    }

  }, [delivery.cityInfo, delivery.editDataDelivery]);

  useEffect(() => () => delivery.clearEditDataDelivery(), [delivery]);

  return (
    <>
      {
        (!isTablet || isEdit) ?
            <>
              {!isCityModalOpen && (
              <form
                  className="delivery-form"
                  onSubmit={delivery.editDataDelivery || delivery.selectedAddress ? handleUpdate : handleSubmit}
              >
                <div className="delivery-form__body">
                  {withSelectCity && (
                      <div className="delivery-form__wrapper">
                        <InputUI
                            className="address-step__input"
                            placeholder="Выберите населенный пункт"
                            value={delivery.cityInfo?.value || ""}
                            disabled
                            onClick={() => setIsCityModalOpen(true)}
                        />
                        {!delivery.cityInfo && (
                            <span className="form-error">
                      Для выбора адреса необходимо указать населенный пункт
                    </span>
                        )}
                      </div>
                  )}

                  <AddressInput
                      text="Адрес*"
                      onChangeAddress={setNewAddressData}
                      disabled={withSelectCity && !delivery.cityInfo}
                      value={newAddressData ? newAddressData.all?.value : ""}
                  />
                  <InputUI
                      text="Квартира/офис"
                      value={apartment}
                      onChange={(e) => setApartment(e.target.value)}
                      disabled={withSelectCity && !delivery.cityInfo}
                  />
                  <InputUI
                      text="Комментарий"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      disabled={withSelectCity && !delivery.cityInfo}
                  />

                  {delivery.hasError && (
                      <div className="delivery-form__informer">
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                              d="M11.25 11.25L11.291 11.23C11.4192 11.1659 11.5631 11.14 11.7057 11.1552C11.8482 11.1703 11.9834 11.2261 12.0952 11.3157C12.2071 11.4054 12.2909 11.5252 12.3368 11.661C12.3826 11.7968 12.3886 11.9429 12.354 12.082L11.646 14.918C11.6111 15.0572 11.6169 15.2034 11.6627 15.3394C11.7084 15.4754 11.7922 15.5954 11.9041 15.6852C12.016 15.775 12.1513 15.8308 12.294 15.846C12.4367 15.8612 12.5807 15.8352 12.709 15.771L12.75 15.75M21 12C21 13.1819 20.7672 14.3522 20.3149 15.4442C19.8626 16.5361 19.1997 17.5282 18.364 18.364C17.5282 19.1997 16.5361 19.8626 15.4442 20.3149C14.3522 20.7672 13.1819 21 12 21C10.8181 21 9.64778 20.7672 8.55585 20.3149C7.46392 19.8626 6.47177 19.1997 5.63604 18.364C4.80031 17.5282 4.13738 16.5361 3.68508 15.4442C3.23279 14.3522 3 13.1819 3 12C3 9.61305 3.94821 7.32387 5.63604 5.63604C7.32387 3.94821 9.61305 3 12 3C14.3869 3 16.6761 3.94821 18.364 5.63604C20.0518 7.32387 21 9.61305 21 12ZM12 8.25H12.008V8.258H12V8.25Z"
                              stroke="#FF0000"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                          />
                        </svg>
                        <span className="delivery-form__informer__text">
                  {delivery.error}
                </span>
                      </div>
                  )}

                </div>

                <ButtonUI
                    className="delivery-form__submit"
                    type="submit"
                    text="Отправить"
                    disabled={!IS_VALID_ADDRESS}
                />

                {notRu && (
                    <div className="delivery-form__informer delivery-form__informer--green">
                      <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                            d="M11.25 11.25L11.291 11.23C11.4192 11.1659 11.5631 11.14 11.7057 11.1552C11.8482 11.1703 11.9834 11.2261 12.0952 11.3157C12.2071 11.4054 12.2909 11.5252 12.3368 11.661C12.3826 11.7968 12.3886 11.9429 12.354 12.082L11.646 14.918C11.6111 15.0572 11.6169 15.2034 11.6627 15.3394C11.7084 15.4754 11.7922 15.5954 11.9041 15.6852C12.016 15.775 12.1513 15.8308 12.294 15.846C12.4367 15.8612 12.5807 15.8352 12.709 15.771L12.75 15.75M21 12C21 13.1819 20.7672 14.3522 20.3149 15.4442C19.8626 16.5361 19.1997 17.5282 18.364 18.364C17.5282 19.1997 16.5361 19.8626 15.4442 20.3149C14.3522 20.7672 13.1819 21 12 21C10.8181 21 9.64778 20.7672 8.55585 20.3149C7.46392 19.8626 6.47177 19.1997 5.63604 18.364C4.80031 17.5282 4.13738 16.5361 3.68508 15.4442C3.23279 14.3522 3 13.1819 3 12C3 9.61305 3.94821 7.32387 5.63604 5.63604C7.32387 3.94821 9.61305 3 12 3C14.3869 3 16.6761 3.94821 18.364 5.63604C20.0518 7.32387 21 9.61305 21 12ZM12 8.25H12.008V8.258H12V8.25Z"
                            stroke="#0b4a0b"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                      </svg>
                      <span className="delivery-form__informer__text">
                  Если вы укажете адрес самостоятельно, менеджер свяжется с Вами
                  для уточнения условий доставки. Укажите улицу и дом.
                </span>
                    </div>
                )}
              </form>
          )} </>
            :
            <div className="delivery-form__select">
              <div className="delivery-form__address">
                {delivery.searchAddress ? delivery.searchAddress?.value : "Выберите на карте или укажите адрес доставки"}
              </div>
              {delivery.hasError && (
                  <div className="delivery-form__informer">
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                          d="M11.25 11.25L11.291 11.23C11.4192 11.1659 11.5631 11.14 11.7057 11.1552C11.8482 11.1703 11.9834 11.2261 12.0952 11.3157C12.2071 11.4054 12.2909 11.5252 12.3368 11.661C12.3826 11.7968 12.3886 11.9429 12.354 12.082L11.646 14.918C11.6111 15.0572 11.6169 15.2034 11.6627 15.3394C11.7084 15.4754 11.7922 15.5954 11.9041 15.6852C12.016 15.775 12.1513 15.8308 12.294 15.846C12.4367 15.8612 12.5807 15.8352 12.709 15.771L12.75 15.75M21 12C21 13.1819 20.7672 14.3522 20.3149 15.4442C19.8626 16.5361 19.1997 17.5282 18.364 18.364C17.5282 19.1997 16.5361 19.8626 15.4442 20.3149C14.3522 20.7672 13.1819 21 12 21C10.8181 21 9.64778 20.7672 8.55585 20.3149C7.46392 19.8626 6.47177 19.1997 5.63604 18.364C4.80031 17.5282 4.13738 16.5361 3.68508 15.4442C3.23279 14.3522 3 13.1819 3 12C3 9.61305 3.94821 7.32387 5.63604 5.63604C7.32387 3.94821 9.61305 3 12 3C14.3869 3 16.6761 3.94821 18.364 5.63604C20.0518 7.32387 21 9.61305 21 12ZM12 8.25H12.008V8.258H12V8.25Z"
                          stroke="#FF0000"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                      />
                    </svg>
                    <span className="delivery-form__informer__text">
                  {delivery.error}
                </span>
                  </div>
              )}
              <ButtonUI
                  className="delivery-form__submit"
                  onClick={() => setIsEdit(true)}
                  text="Уточнить адрес"
              />
            </div>
      }


      {withSelectCity && isCityModalOpen && (
        <CitySelectionModal
          cityForOrder={cityForOrder}
          isOpen={isCityModalOpen}
          closeModal={() => setIsCityModalOpen(false)}
          setSityHandler={handleCitySelect}
        />
      )}
    </>
  );
}

export default observer(DeliveryFormBase);
